import { useState, useCallback, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth, useUser } from "@clerk/react";

import { OnboardingLayout } from "@/components/onboarding/OnboardingLayout";
import { PersonalizeStep } from "@/components/onboarding/PersonalizeStep";
import { ReferralStep } from "@/components/onboarding/ReferralStep";
import { RoleStep } from "@/components/onboarding/RoleStep";
import { ContentGoalsStep } from "@/components/onboarding/ContentGoalsStep";
import { PricingStep } from "@/components/onboarding/PricingStep";
import { SetupTransitionStep } from "@/components/onboarding/SetupTransitionStep";
import { IntroAnimation } from "@/components/onboarding/IntroAnimation";
import { CheckoutSuccessModal } from "@/components/billing/CheckoutSuccessModal";
import { createOnboardingApi } from "@/lib/api/onboarding";
import { createCreditsApi } from "@/lib/api/credits";
import { useCreditsBalance } from "@/lib/hooks/use-credits";

const TOTAL_STEPS = 6;
const STORAGE_KEY = "rayloop_onboarding";

interface OnboardingData {
  name: string;
  ageConfirmed: boolean;
  marketingAccepted: boolean;
  referralSource: string | null;
  role: string | null;
  contentGoals: string[];
  selectedPlan: string | null;
}

function loadSavedState(): { step: number; data: OnboardingData } | null {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (raw) return JSON.parse(raw);
  } catch {
    // ignore corrupt data
  }
  return null;
}

const DEFAULT_DATA: OnboardingData = {
  name: "",
  ageConfirmed: false,
  marketingAccepted: false,
  referralSource: null,
  role: null,
  contentGoals: [],
  selectedPlan: null,
};

export function OnboardingPage() {
  const navigate = useNavigate();

  const { getToken } = useAuth();
  const { user } = useUser();
  const api = createOnboardingApi({ getToken });
  const { data: balance } = useCreditsBalance();
  const [isSelectingPlan, setIsSelectingPlan] = useState<string | null>(null);

  const saved = loadSavedState();
  const [step, setStep] = useState(() => {
    const base = saved?.step ?? 0;
    // If returning from Stripe checkout, lock to the pricing step
    if (new URLSearchParams(window.location.search).get("checkout") === "success") return 4;
    return base;
  });
  const [data, setData] = useState<OnboardingData>(saved?.data ?? DEFAULT_DATA);
  const [hydrated, setHydrated] = useState(!!saved);

  // Only show intro if no saved state exists (fresh start)
  const [showIntro, setShowIntro] = useState(!saved);

  /** On mount, hydrate from backend profile if no session state exists. */
  useEffect(() => {
    if (saved) return; // sessionStorage takes priority
    api
      .getProfile()
      .then((profile) => {
        // If already completed, redirect to dashboard
        if (profile.completed_at) {
          navigate("/dashboard", { replace: true });
          return;
        }
        // Merge backend data into local state
        setData((prev) => ({
          ...prev,
          name: profile.name ?? prev.name,
          ageConfirmed: profile.age_confirmed ?? prev.ageConfirmed,
          marketingAccepted:
            profile.marketing_accepted ?? prev.marketingAccepted,
          referralSource: profile.referral_source ?? prev.referralSource,
          role: profile.role ?? prev.role,
          contentGoals: profile.content_goals ?? prev.contentGoals,
          selectedPlan: profile.selected_plan ?? prev.selectedPlan,
        }));
      })
      .catch(() => {
        // If fetch fails, continue with defaults
      })
      .finally(() => setHydrated(true));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  /** Persist step + data to sessionStorage whenever either changes. */
  useEffect(() => {
    if (!hydrated) return;
    try {
      sessionStorage.setItem(STORAGE_KEY, JSON.stringify({ step, data }));
    } catch {
      // quota errors — non-critical
    }
  }, [step, data, hydrated]);

  function updateData(partial: Partial<OnboardingData>) {
    setData((d) => ({ ...d, ...partial }));
  }

  /** Save current screen data to backend, then advance. */
  async function saveAndAdvance(fields: Record<string, unknown>) {
    setStep((s) => s + 1);
    try {
      await api.updateProfile(fields);
    } catch {
      // Non-blocking — continue even if save fails
    }
  }

  function handleBack() {
    setStep((s) => Math.max(0, s - 1));
  }

  function handleSkip() {
    setStep((s) => s + 1);
  }

  /** Initiate Stripe checkout for a paid plan. */
  async function handleSelectPlan(
    planSlug: "pro" | "max",
    billing: "monthly" | "annual",
  ) {
    setIsSelectingPlan(planSlug);
    try {
      const creditsApi = createCreditsApi({ getToken });
      const returnUrl = `${window.location.origin}/onboarding?checkout=success`;
      const { url } = await creditsApi.subscribe(planSlug, billing, returnUrl);
      window.location.assign(url);
    } catch {
      setIsSelectingPlan(null);
    }
  }

  /** Called when CheckoutSuccessModal confirms the plan upgrade. */
  function handleCheckoutSuccess() {
    saveAndAdvance({ selected_plan: balance?.plan ?? "pro" });
  }

  /** Final step callback: complete onboarding and redirect. */
  const handleComplete = useCallback(async () => {
    try {
      await api.complete();
      // Reload Clerk user so publicMetadata.onboardingComplete is fresh
      // before ProtectedRoute checks it on the next route
      await user?.reload();
      sessionStorage.removeItem(STORAGE_KEY);
      sessionStorage.setItem("rayloop_start_tour", "1");
      navigate("/dashboard", { replace: true });
    } catch {
      await user?.reload();
      sessionStorage.removeItem(STORAGE_KEY);
      navigate("/dashboard", { replace: true });
    }
  }, [api, navigate, user]);

  // Handlers for each screen's "next" action
  function handlePersonalizeNext() {
    saveAndAdvance({
      name: data.name || null,
      age_confirmed: data.ageConfirmed,
      marketing_accepted: data.marketingAccepted,
    });
  }

  function handleReferralSelect(value: string) {
    updateData({ referralSource: value });
    saveAndAdvance({ referral_source: value });
  }

  function handleRoleSelect(value: string) {
    updateData({ role: value });
    saveAndAdvance({ role: value });
  }

  function handleContentGoalsNext() {
    saveAndAdvance({ content_goals: data.contentGoals });
  }

  // Setup transition (last step) — fullscreen, no layout wrapper
  if (step === 5) {
    return (
      <div className="flex min-h-svh items-center justify-center bg-background">
        <SetupTransitionStep onComplete={handleComplete} />
      </div>
    );
  }

  if (showIntro) {
    return <IntroAnimation onComplete={() => setShowIntro(false)} />;
  }

  // Determine dynamic skip behavior based on current step
  const isContentGoalsActive = step === 3 && data.contentGoals.length > 0;

  const currentSkipAction = isContentGoalsActive
    ? handleContentGoalsNext
    : handleSkip;
  const currentSkipText = isContentGoalsActive ? "Continue" : "Skip";
  const isSkipPrimary = isContentGoalsActive;

  return (
    <>
    <OnboardingLayout
      currentStep={step}
      totalSteps={TOTAL_STEPS}
      onBack={handleBack}
      onSkip={currentSkipAction}
      showBack={step > 0}
      showSkip={step > 0 && step < 5}
      skipText={currentSkipText}
      isSkipPrimary={isSkipPrimary}
    >
      {step === 0 && (
        <PersonalizeStep
          value={data.name}
          ageConfirmed={data.ageConfirmed}
          marketingAccepted={data.marketingAccepted}
          onChange={(name) => updateData({ name })}
          onAgeConfirmedChange={(ageConfirmed) => updateData({ ageConfirmed })}
          onMarketingAcceptedChange={(marketingAccepted) =>
            updateData({ marketingAccepted })
          }
          onNext={handlePersonalizeNext}
        />
      )}

      {step === 1 && (
        <ReferralStep
          value={data.referralSource}
          onChange={handleReferralSelect}
        />
      )}

      {step === 2 && <RoleStep value={data.role} onChange={handleRoleSelect} />}

      {step === 3 && (
        <ContentGoalsStep
          value={data.contentGoals}
          onChange={(contentGoals) => updateData({ contentGoals })}
        />
      )}

      {step === 4 && (
        <PricingStep
          currentPlan={balance?.plan ?? "free"}
          onSelectPlan={handleSelectPlan}
          isSelectingPlan={isSelectingPlan}
        />
      )}
    </OnboardingLayout>
    <CheckoutSuccessModal onSuccess={handleCheckoutSuccess} />
    </>
  );
}
